/**
 * Spec 027 — TeamWaiting (joiner, step 5/6).
 *
 * V2 rebuild against Figma frame a_start_waiting_for_team (393×884).
 * Functional contract (hooks, navigation, WS handlers, step info) is
 * preserved verbatim from the V1 implementation.
 */
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import ClavisCircularProgress from '../../components/ClavisCircularProgress';
import OnboardingShellV2 from '../../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../../components/ProgressIndicatorV2';
import heroWaiting from '../../assets/onboarding/hero-team-waiting.svg';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useWizardState } from '../../wizard/useWizardState';
import { getStepInfo } from '../../wizard/wizardSteps';

const TeamWaitingStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { code: rallyeCode } = useParams<{ code: string }>();
  const wizard = useWizardState();
  const tf = wizard.teamFlow;

  const step = getStepInfo('team-waiting', 'with-code');
  const canSubscribe = !!wizard.organizationSlug && !!tf.runId;

  useWebSocket({
    path:
      canSubscribe
        ? `/api/orgs/${wizard.organizationSlug}/runs/${tf.runId}/ws`
        : '',
    enabled: canSubscribe,
    handlers: {
      'team.stage_changed': (data) => {
        const payload = data as { team_id?: string; stage?: string } | undefined;
        if (!payload || payload.team_id !== tf.teamId) return;
        if (payload.stage === 'naming') {
          navigate(`/join/${rallyeCode}/team/pending`);
        }
      },
      'team.name_updated': (data) => {
        const payload = data as { team_id?: string; name?: string | null } | undefined;
        if (!payload || payload.team_id !== tf.teamId) return;
        if (payload.name && tf.runId) {
          if (tf.teamId) {
            try {
              sessionStorage.setItem(`clavis_team_for_run_${tf.runId}`, tf.teamId);
            } catch { /* sessionStorage disabled */ }
          }
          navigate(`/runs/${tf.runId}/participant`);
        }
      },
    },
  });

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBlockStart: '52px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroWaiting}
          alt=""
          sx={{
            width: '107px',
            height: '98px',
            display: 'block',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '28px',
            fontWeight: 600,
            lineHeight: '36px',
            color: '#322F35',
            textAlign: 'center',
            margin: 0,
            whiteSpace: 'pre-line',
          }}
        >
          {t('waiting.title')}
        </Typography>
        <Box
          role="status"
          aria-live="polite"
          aria-label={t('waiting.subtitle')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <ClavisCircularProgress indeterminate ariaLabel={t('waiting.subtitle')} />
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.5px',
              color: '#322F35',
              textAlign: 'center',
              // Half-leading comp (figma-implement skill §6 sub-drift):
              // body/large CSS line-box has 3 px half-leading above the
              // glyph, Figma's text-bbox has 0. Pull the element up so
              // the glyph lands at Figma's y=327.
              marginBlockStart: '-3px',
            }}
          >
            {t('waiting.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate(`/join/${rallyeCode}/team/find`)}
          sx={{
            // Cascade comp: Body's -3 half-leading shrunk the Inner Box by 3,
            // propagating an upward shift to this button. +3 restores the
            // Figma pill at y=378.
            marginBlockStart: '3px',
            minWidth: 0,
            // Figma Button-outline visible pill is 40 px tall. The project's
            // theme.ts MuiButton.styleOverrides.root sets minHeight: 48,
            // which silently wins over sx.height because CSS computes
            // effective-height = max(height, min-height). Explicit minHeight
            // here defeats that.
            minHeight: '40px',
            height: '40px',
            paddingInline: '16px',
            borderRadius: '100px',
            borderColor: '#CAC4D0',
            color: '#49454F',
            fontFamily: "'Roboto', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#49454F',
              backgroundColor: 'rgba(73, 69, 79, 0.04)',
            },
          }}
        >
          {t('waiting.back')}
        </Button>
      </Box>
    </OnboardingShellV2>
  );
};

export default TeamWaitingStep;
